import React, { type ReactElement } from 'react'
import { Navigate, useRoutes } from 'react-router'

import FallbackComponent from 'common/components/FallbackComponent/FallbackComponent'

import { PATH_SEGMENT } from './pathSegments'

export type RoutesConfigType = Record<string, ReactElement>

export type RouteObject = {
  path: string
  element: ReactElement
  [key: string]: unknown
}

export const generateRoute = (
  path: string,
  element: ReactElement,
  routeProps = {},
  hasErrorBoundary = true,
): RouteObject => {
  const resolvedElement = (
    <FallbackComponent hasErrorBoundary={hasErrorBoundary}>{element}</FallbackComponent>
  )

  return {
    path,
    element: resolvedElement,
    ...routeProps,
  }
}

export const GenerateRouter: React.FC<{
  routesConfig: RoutesConfigType
  includeErrorRoute?: boolean
}> = ({ routesConfig, includeErrorRoute = true }) => {
  // Convert config to routes array for React Router v7's useRoutes hook
  const routes = Object.entries(routesConfig).map(([path, element]: [string, ReactElement]) =>
    generateRoute(path, element),
  )

  if (includeErrorRoute) {
    routes.push({
      path: '*',
      element: <Navigate to={PathUtils.getError('404')} replace />,
    })
  }

  return useRoutes(routes)
}

export const generateRouter = (
  routesConfig: RoutesConfigType,
  includeErrorRoute = true,
): ReactElement => {
  return <GenerateRouter routesConfig={routesConfig} includeErrorRoute={includeErrorRoute} />
}

export const PathUtils = {
  _errorRoute: <></>,
  get errorRoute() {
    this._errorRoute = this._errorRoute || this.getErrorRoute()
    return this._errorRoute
  },
  getErrorRoute() {
    return <></>
  },

  getHome(siteId: string): string {
    return `/${siteId}/${PATH_SEGMENT.HOME}`
  },

  getFaq(siteId: string): string {
    return `/${siteId}/${PATH_SEGMENT.FAQ}`
  },

  getProfile(siteId: string): string {
    return `/${siteId}/${PATH_SEGMENT.PROFILE}`
  },

  getAuth(): string {
    return [PATH_SEGMENT.ROOT, PATH_SEGMENT.AUTH].join('')
  },

  getLogin(): string {
    return [this.getAuth(), PATH_SEGMENT.LOGIN].join('/')
  },

  getResetPassword(): string {
    return [this.getAuth(), PATH_SEGMENT.RESET_PASSWORD].join('/')
  },

  getValidateEmail(email: string): string {
    return [this.getAuth(), PATH_SEGMENT.VALIDATE_EMAIL, email].join('/')
  },

  getRegister(): string {
    return [this.getAuth(), PATH_SEGMENT.REGISTER].join('/')
  },

  getTermsAndConditions(): string {
    return [this.getAuth(), PATH_SEGMENT.TERMS_AND_CONDITIONS].join('/')
  },

  getScreenPopPrivacy(): string {
    return [PATH_SEGMENT.ROOT, PATH_SEGMENT.SCREEN_POP_PRIVACY].join('')
  },

  getValidateIdentity(): string {
    return [PATH_SEGMENT.ROOT, PATH_SEGMENT.VALIDATE_IDENTITY].join('')
  },

  getValidationProcess(): string {
    return [PATH_SEGMENT.ROOT, PATH_SEGMENT.VALIDATION_PROCESSING].join('')
  },

  getProvider(siteId: string): string {
    return `/${siteId}/${PATH_SEGMENT.PROVIDER}`
  },

  getUserTermsAndConditions(siteId: string): string {
    return [this.getProvider(siteId), PATH_SEGMENT.TERMS_AND_CONDITIONS].join('/')
  },

  getProviderDictionary(siteId: string): string {
    return [this.getProvider(siteId), PATH_SEGMENT.DICTIONARY].join('/')
  },

  getPersonalProfile(siteId: string): string {
    return [this.getProfile(siteId), PATH_SEGMENT.PERSONAL_PROFILE].join('/')
  },

  getAdmin(): string {
    return [PATH_SEGMENT.ROOT, PATH_SEGMENT.ADMIN].join('')
  },

  getGuest(): string {
    return [PATH_SEGMENT.ROOT, PATH_SEGMENT.GUEST].join('')
  },

  getBookingDetails(siteId: string, bookingId: string): string {
    return [this.getHome(siteId), PATH_SEGMENT.BOOKING, bookingId, PATH_SEGMENT.DETAILS].join('/')
  },
  getBookingShop(siteId: string, bookingId: string): string {
    return [this.getHome(siteId), PATH_SEGMENT.BOOKING, bookingId, PATH_SEGMENT.SHOP].join('/')
  },
  getBookingContracts(siteId: string, bookingId: string): string {
    return [this.getHome(siteId), PATH_SEGMENT.BOOKING, bookingId, PATH_SEGMENT.CONTRACTS].join('/')
  },

  getPromotionContracts(siteId: string, bookingId: string): string {
    return [this.getHome(siteId), PATH_SEGMENT.PACKAGE, bookingId, PATH_SEGMENT.CONTRACTS].join('/')
  },

  getPatientSchedule(siteId: string, bookingId: string): string {
    return [this.getHome(siteId), PATH_SEGMENT.BOOKING, bookingId, PATH_SEGMENT.SCHEDULE].join('/')
  },

  getBook(siteId: string): string {
    return [this.getHome(siteId), PATH_SEGMENT.BOOK, siteId].join('/')
  },

  getSchedule(siteId: string, date?: string): string {
    return [`/${siteId}`, `${PATH_SEGMENT.SCHEDULE}${date ? `/${date}` : ''}`].join('/')
  },

  getScheduleSeries(siteId: string): string {
    return [this.getSchedule(siteId), PATH_SEGMENT.SERIES].join('/')
  },

  getScheduleSeriesTemplates(siteId: string): string {
    return [this.getScheduleSeries(siteId), PATH_SEGMENT.TEMPLATES].join('/')
  },

  getGuestBook(siteId: string): string {
    return [this.getGuest(siteId), PATH_SEGMENT.HOME, PATH_SEGMENT.BOOK, siteId].join('/')
  },

  getCheckout(siteId: string): string {
    return `/${siteId}/${PATH_SEGMENT.CHECKOUT}`
  },

  getPromotionCheckout(siteId: string, bookingId: string): string {
    return `/${siteId}/package/${bookingId}/${PATH_SEGMENT.CHECKOUT}`
  },

  getPromotionDetails(siteId: string, bookingId: string): string {
    return [
      this.getHome(siteId),
      PATH_SEGMENT.PACKAGE,
      bookingId,
      PATH_SEGMENT.DETAILS,
      PATH_SEGMENT.ROOMS,
    ].join('/')
  },

  getPromotion(siteId: string, bookingId: string): string {
    return [this.getHome(siteId), PATH_SEGMENT.PACKAGE, bookingId].join('/')
  },

  getPromotionSchedule(siteId: string, bookingId: string): string {
    return [this.getHome(siteId), PATH_SEGMENT.PACKAGE, bookingId, PATH_SEGMENT.SCHEDULE].join('/')
  },
  getPromotionShop(siteId: string, bookingId: string): string {
    return [this.getHome(siteId), PATH_SEGMENT.PACKAGE, bookingId, PATH_SEGMENT.SHOP].join('/')
  },

  getTicketDetails(siteId: string, ticketId: string): string {
    return `/${siteId}/${PATH_SEGMENT.SUPPORT}/${PATH_SEGMENT.TICKET_DETAILS}/${ticketId}`
  },

  getError(code: string): string {
    return `/${[PATH_SEGMENT.ERROR, code].join('/')}`
  },
  getDynamicRoute(siteId: string, route: string): string {
    return `/${siteId}/${route}`
  },
  getCharts(siteId: string, patientId: string | number, encounterId?: string | null): string {
    const params = new URLSearchParams({ id: String(patientId) })
    if (encounterId) {
      params.set('encounterId', encounterId)
    }
    return `/${siteId}/${PATH_SEGMENT.CHARTS}?${params.toString()}`
  },
}
